import React, { useState } from 'react';
import {
    UserPlusIcon,
    UserMinusIcon,
    CrownIcon,
    PencilIcon,
    EyeIcon,
    CaretDownIcon,
    CheckIcon,
    XIcon,
    SpinnerIcon,
    UsersIcon
} from '@phosphor-icons/react';
import { useProject } from '../../context/ProjectContext';
import { useAuth } from '../../context/AuthContext';
import { ProjectMember } from '../../lib/projectMemberService';

interface UsersPanelProps {
    projectId: string;
    className?: string;
}

export const UsersPanel: React.FC<UsersPanelProps> = ({ projectId, className = '' }) => {
    const { user } = useAuth();
    const {
        projectMembers,
        membersLoading,
        inviteMember,
        removeMember,
        updateMemberRole,
        projects
    } = useProject();

    const [inviteUsername, setInviteUsername] = useState('');
    const [inviteRole, setInviteRole] = useState<'viewer' | 'editor'>('viewer');
    const [inviting, setInviting] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [showRoleDropdown, setShowRoleDropdown] = useState<string | null>(null);

    // Get current project owner
    const currentProject = projects.find(p => p.id === projectId);
    const isOwner = currentProject?.user_id === user?.id;

    // Filter members
    const acceptedMembers = projectMembers.filter(m => m.status === 'accepted');
    const pendingMembers = projectMembers.filter(m => m.status === 'pending');

    // Handle invite
    const handleInvite = async () => {
        if (!inviteUsername.trim()) return;

        setInviting(true);
        setInviteError(null);

        const result = await inviteMember(inviteUsername.trim(), inviteRole);

        if (result.success) {
            setInviteUsername('');
        } else {
            setInviteError(result.error || 'Failed to invite user');
        }

        setInviting(false);
    };

    // Handle role change
    const handleRoleChange = async (memberId: string, newRole: 'viewer' | 'editor') => {
        await updateMemberRole(memberId, newRole);
        setShowRoleDropdown(null);
    };

    // Handle remove
    const handleRemove = async (memberId: string) => {
        if (confirm('Remove this member from the project?')) {
            await removeMember(memberId);
        }
    };

    if (!isOwner) {
        return null; // Only owner can see this panel
    }

    return (
        <div className={`flex flex-col h-full bg-onyx ${className}`}>
            {/* Header */}
            <div className="p-4 border-b border-white/5">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <UsersIcon className="w-4 h-4 text-indigo-400" />
                    Project Members
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                    Manage who can access this project
                </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
                {/* Owner Section */}
                <div>
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
                        Owner
                    </h4>
                    <div className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/30">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                            {user?.avatar_url ? (
                                <img src={user.avatar_url} className="w-full h-full rounded-full object-cover" />
                            ) : (
                                <CrownIcon className="w-4 h-4 text-white" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">
                                @{user?.username || 'You'}
                            </p>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-400">
                            OWNER
                        </span>
                    </div>
                </div>

                {/* Members Section */}
                <div>
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
                        Members ({acceptedMembers.length})
                    </h4>

                    {membersLoading ? (
                        <div className="flex items-center justify-center py-4">
                            <SpinnerIcon className="w-5 h-5 text-zinc-500 animate-spin" />
                        </div>
                    ) : acceptedMembers.length === 0 ? (
                        <p className="text-sm text-zinc-600 py-4 text-center">
                            No members yet
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {acceptedMembers.map(member => (
                                <MemberRow
                                    key={member.id}
                                    member={member}
                                    showRoleDropdown={showRoleDropdown === member.id}
                                    onToggleDropdown={() => setShowRoleDropdown(
                                        showRoleDropdown === member.id ? null : member.id
                                    )}
                                    onRoleChange={(role) => handleRoleChange(member.id, role)}
                                    onRemove={() => handleRemove(member.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Pending Invitations */}
                {pendingMembers.length > 0 && (
                    <div>
                        <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
                            Pending Invitations ({pendingMembers.length})
                        </h4>
                        <div className="space-y-2">
                            {pendingMembers.map(member => (
                                <div
                                    key={member.id}
                                    className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/30 border border-dashed border-zinc-700"
                                >
                                    <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                                        <span className="text-xs text-zinc-400">
                                            {member.user?.username?.[0]?.toUpperCase() || '?'}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-zinc-400 truncate">
                                            @{member.user?.username || 'Unknown'}
                                        </p>
                                        <p className="text-[10px] text-zinc-600">Pending</p>
                                    </div>
                                    <span className={`
                                        px-2 py-0.5 rounded-full text-[10px] font-bold
                                        ${member.role === 'editor'
                                            ? 'bg-indigo-500/20 text-indigo-400'
                                            : 'bg-zinc-500/20 text-zinc-400'
                                        }
                                    `}>
                                        {member.role === 'editor' ? 'EDITOR' : 'VIEWER'}
                                    </span>
                                    <button
                                        onClick={() => handleRemove(member.id)}
                                        className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                        title="Cancel invitation"
                                    >
                                        <XIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Invite Form */}
                <div>
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
                        Invite Member
                    </h4>
                    <div className="space-y-2">
                        <input
                            type="text"
                            value={inviteUsername}
                            onChange={(e) => {
                                setInviteUsername(e.target.value);
                                setInviteError(null);
                            }}
                            placeholder="Enter username..."
                            className="w-full px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-500"
                        />

                        <div className="flex gap-2">
                            {/* Role Selector */}
                            <div className="relative flex-1">
                                <button
                                    onClick={() => setInviteRole(inviteRole === 'viewer' ? 'editor' : 'viewer')}
                                    className="w-full px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-sm text-white flex items-center justify-between hover:border-zinc-600 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        {inviteRole === 'editor' ? (
                                            <PencilIcon className="w-4 h-4 text-indigo-400" />
                                        ) : (
                                            <EyeIcon className="w-4 h-4 text-zinc-400" />
                                        )}
                                        {inviteRole === 'editor' ? 'Editor' : 'View Only'}
                                    </span>
                                    <CaretDownIcon className="w-4 h-4 text-zinc-500" />
                                </button>
                            </div>

                            {/* Invite Button */}
                            <button
                                onClick={handleInvite}
                                disabled={!inviteUsername.trim() || inviting}
                                className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg flex items-center gap-2 transition-colors"
                            >
                                {inviting ? (
                                    <SpinnerIcon className="w-4 h-4 animate-spin" />
                                ) : (
                                    <UserPlusIcon className="w-4 h-4" />
                                )}
                                Invite
                            </button>
                        </div>

                        {inviteError && (
                            <p className="text-xs text-red-400">{inviteError}</p>
                        )}

                        {/* Role Descriptions */}
                        <div className="mt-3 p-3 bg-zinc-800/30 rounded-lg">
                            <p className="text-xs text-zinc-500 mb-2">Permissions:</p>
                            <div className="space-y-1.5">
                                <div className="flex items-start gap-2">
                                    <EyeIcon className="w-3.5 h-3.5 text-zinc-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-zinc-400 font-medium">View Only</p>
                                        <p className="text-[10px] text-zinc-600">
                                            Can chat, view all pages, and change songs in TEMP
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <PencilIcon className="w-3.5 h-3.5 text-indigo-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-indigo-400 font-medium">Editor</p>
                                        <p className="text-[10px] text-zinc-600">
                                            Full access except deleting the project
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Member Row Component
interface MemberRowProps {
    member: ProjectMember;
    showRoleDropdown: boolean;
    onToggleDropdown: () => void;
    onRoleChange: (role: 'viewer' | 'editor') => void;
    onRemove: () => void;
}

const MemberRow: React.FC<MemberRowProps> = ({
    member,
    showRoleDropdown,
    onToggleDropdown,
    onRoleChange,
    onRemove
}) => {
    return (
        <div className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors group">
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                {member.user?.avatar_url ? (
                    <img src={member.user.avatar_url} className="w-full h-full object-cover" />
                ) : (
                    <span className="text-xs text-zinc-400">
                        {member.user?.username?.[0]?.toUpperCase() || '?'}
                    </span>
                )}
            </div>

            {/* Name */}
            <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                    @{member.user?.username || 'Unknown'}
                </p>
            </div>

            {/* Role Badge/Dropdown */}
            <div className="relative">
                <button
                    onClick={onToggleDropdown}
                    className={`
                        px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 transition-colors
                        ${member.role === 'editor'
                            ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30'
                            : 'bg-zinc-500/20 text-zinc-400 hover:bg-zinc-500/30'
                        }
                    `}
                >
                    {member.role === 'editor' ? (
                        <PencilIcon className="w-3 h-3" />
                    ) : (
                        <EyeIcon className="w-3 h-3" />
                    )}
                    {member.role === 'editor' ? 'EDITOR' : 'VIEWER'}
                    <CaretDownIcon className="w-3 h-3" />
                </button>

                {/* Role Dropdown */}
                {showRoleDropdown && (
                    <div className="absolute right-0 top-full mt-1 w-32 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 overflow-hidden">
                        <button
                            onClick={() => onRoleChange('viewer')}
                            className={`
                                w-full px-3 py-2 text-xs text-left flex items-center gap-2 hover:bg-zinc-700 transition-colors
                                ${member.role === 'viewer' ? 'text-white' : 'text-zinc-400'}
                            `}
                        >
                            <EyeIcon className="w-3.5 h-3.5" />
                            View Only
                            {member.role === 'viewer' && <CheckIcon className="w-3.5 h-3.5 ml-auto" />}
                        </button>
                        <button
                            onClick={() => onRoleChange('editor')}
                            className={`
                                w-full px-3 py-2 text-xs text-left flex items-center gap-2 hover:bg-zinc-700 transition-colors
                                ${member.role === 'editor' ? 'text-indigo-400' : 'text-zinc-400'}
                            `}
                        >
                            <PencilIcon className="w-3.5 h-3.5" />
                            Editor
                            {member.role === 'editor' && <CheckIcon className="w-3.5 h-3.5 ml-auto text-indigo-400" />}
                        </button>
                    </div>
                )}
            </div>

            {/* Remove Button */}
            <button
                onClick={onRemove}
                className="p-1.5 text-zinc-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors opacity-0 group-hover:opacity-100"
                title="Remove member"
            >
                <UserMinusIcon className="w-4 h-4" />
            </button>
        </div>
    );
};

export default UsersPanel;
